import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MyPlanDetail } from "components/features/MyPlanDetails/MyPlanDetail";
import { deleteActionCardAsync, fetchMyPlanDetailAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { axiosGet, axiosPatch, axiosPost } from "utils/APIHelper";
import { pageNames, responseTypes, serviceTypes } from "utils/Types";

interface AppState {
  isSmeUser: boolean;
  isSmeUserFetched: boolean;
  isAbovePropertyUserFetched: boolean;
  innCode: string;
  selectedTab: number;
  searchSelectedTab: number;
  filters: any;
  selectedFilters: any;
  filterIds: string[];
  selectedFunctionalAreas: string[];
  selectedPlanSources: string[];
  selectedPlanType: number[];
  selectedInncodes: string[];
  selectedAdminInncodes: string[];
  selectedBrand: string[];
  selectedStatus: string[];
  openSectionsList: {};
  selectedCards: any;
  isAdmin: boolean;
  loadPages: boolean;
  lobbyUser: string;
  searchText: string;
  cardsCompletedPrev: any;
  activeActionCard: any;
  userName: any;
  isCopyActionEnabled: boolean;
  searchActive: boolean;
  innCodes: any;
  innCodeFetching: boolean;
  filtersApplied: boolean;
  userRoles: any;
  editEmailLoader: boolean;
  selectedUser: string;
  isAbovePropertyUser: boolean;
  loggedInUserDetails: {
    accessRoles: any;
  };
  impersonationDetails: {
    isImpersonated: boolean;
    userDetails: any;
    navigateToURL: string;
  };
  navSelectedDetails: {
    leftNavSelected: string;
  };
}

const initialState: AppState = {
  loggedInUserDetails: {
    accessRoles: {}
  },
  impersonationDetails: {
    isImpersonated: false,
    navigateToURL: "",
    userDetails: {}
  },
  navSelectedDetails: {
    leftNavSelected: ""
  },
  isSmeUser: false,
  isAdmin: false,
  isAbovePropertyUserFetched: false,
  isSmeUserFetched: false,
  isAbovePropertyUser: false,
  selectedTab: 0,
  searchSelectedTab: 0,
  filters: [],
  selectedFilters: [],
  selectedFunctionalAreas: [],
  selectedPlanSources: [],
  selectedPlanType: [],
  selectedInncodes: [],
  selectedAdminInncodes: [],
  selectedBrand: [],
  selectedStatus: [],
  filterIds: [],
  innCode: "",
  lobbyUser: "",
  searchText: "",
  loadPages: true,
  openSectionsList: {},
  selectedCards: { cardList: [], sectionId: "" },
  cardsCompletedPrev: -1,
  activeActionCard: {},
  userName: {},
  isCopyActionEnabled: false,
  searchActive: false,
  innCodes: [],
  innCodeFetching: true,
  filtersApplied: false,
  userRoles: { roles: [], filters: [] },
  editEmailLoader: false,
  selectedUser: ""
};

export const fetchFilters = createAsyncThunk("plans/filters", async (thunkApi) => {
  const API_URL = "plans/filters";
  const response: any = await axiosGet(API_URL);
  return response.data;
});

export const fetchSMEUser = createAsyncThunk("smeuser", async (thunkApi: any) => {
  const API_URL = "smeuser";
  const response: any = await axiosGet(API_URL, serviceTypes.user);
  if (response.response?.status === responseTypes.not_found) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const submitUserLoggedInDate = createAsyncThunk("userloggedindate", async (thunkApi: any) => {
  const API_URL = `associates/metadata?update_log_in_timestamp=true`;
  const response: any = await axiosPatch(API_URL, serviceTypes.user);
  if (response.response?.status === responseTypes.not_found) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchLoggedInUserInfoAsync = createAsyncThunk(`loggedInUser`, async (data: any, thunkApi) => {
  const API_URL = `associates?size=${data.size}&hltIds=${data.value}`;
  const response: any = await axiosGet(API_URL, "user");
  if (response.response?.status === responseTypes.not_found) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const editPreferredEmail = createAsyncThunk("editPreferredEmail", async (data: any, thunkApi) => {
  const API_URL = `associates`;
  const response: any = await axiosPatch(API_URL, "user", { ...data });
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response;
});

export const fetchInncodes = createAsyncThunk(`fetchInncodes`, async (data: string, thunkApi) => {
  const API_URL = `innCodes?searchText=${data}`;

  const response = await axiosGet(API_URL, "");

  return response.data;
});
export const fetchRoles = createAsyncThunk(`fetchRoles`, async (data: any, thunkApi) => {
  const API_URL = `roles`;
  const response = await axiosGet(API_URL, "user");

  return response.data;
});
export const editRoleSelection = createAsyncThunk(`editRoleSelection`, async (data: any, thunkApi) => {
  const API_URL = `associates/roles`;
  const response = await axiosPost(API_URL, "user", data);

  return response.data;
});

export const fetchAllowedRoles = createAsyncThunk(`allowed-roles`, async (thunkApi: any) => {
  const API_URL = `allowed-roles`;
  const response = await axiosGet(API_URL, serviceTypes.user);
  if (
    response?.response?.status >= responseTypes.internal_error &&
    response?.response?.status < responseTypes.other_error
  ) {
    return thunkApi.rejectWithValue(response.response);
  }
  return response.data;
});

export const AppSlice = createSlice({
  name: "App",
  initialState,
  reducers: {
    setImpersonationDetails: (state, action) => {
      state.isAbovePropertyUser = action.payload.userDetails.isAbovePropertyUser;
      state.isAdmin = !action.payload.isImpersonated;
      state.isSmeUser = action.payload.isImpersonated ? !action.payload.isImpersonated : state.userName.isSmeUser;
      state.impersonationDetails = action.payload;
    },
    setCardsCompletedPrev: (state, action) => {
      state.cardsCompletedPrev = action.payload;
    },
    librarySelectedTab: (state, action) => {
      if (action.payload === pageNames.libraryHall || pageNames.allPlans || pageNames.bookmarked) {
        state.searchActive = false;
      }
      state.selectedTab = action.payload;
      state.selectedFilters = [];
      state.filterIds = [];
      state.selectedFunctionalAreas = [];
      state.selectedPlanType = [];
      state.selectedInncodes = [];
      state.selectedAdminInncodes = [];
      state.selectedStatus = [];
      state.selectedBrand = [];
      state.selectedUser = "";
      state.searchText = "";
      state.filtersApplied = false;
      state.selectedPlanSources = [];
    },
    clearAllFilters: (state) => {
      state.selectedFilters = [];
      state.filterIds = [];
      state.selectedFunctionalAreas = [];
      state.selectedPlanType = [];
      state.selectedInncodes = [];
      state.selectedAdminInncodes = [];
      state.selectedStatus = [];
      state.selectedBrand = [];
      state.selectedFilters = [];
      state.filterIds = [];
      state.selectedUser = "";
      state.searchText = "";
      state.filtersApplied = false;
      state.selectedPlanSources = [];
    },
    setFiltersApplied: (state, action) => {
      state.filtersApplied = action.payload;
    },
    searchSelectedTab: (state, action) => {
      state.searchSelectedTab = action.payload;
    },
    setSelectedFilters: (state, action) => {
      state.selectedFilters = action.payload;
      state.filterIds = action.payload.map((item: any) => item.checked).flat();
      if (state.filterIds.length > 0) {
        state.filtersApplied = true;
      }
    },
    clearSelectedFilters: (state) => {
      state.selectedFilters = [];
      state.filterIds = [];
    },

    setSelectedFunctionalAreas: (state, action) => {
      state.selectedFunctionalAreas = action.payload;
    },
    setSearchText: (state, action) => {
      state.searchText = action.payload;
    },
    setSelectedPlanSources: (state, action) => {
      state.selectedPlanSources = action.payload;
    },
    setSelectedPlanType: (state, action) => {
      state.selectedPlanType = action.payload;
    },
    setSelectedInncodes: (state, action) => {
      state.selectedInncodes = action.payload;
    },

    setSelectedBrand: (state, action) => {
      state.selectedBrand = action.payload;
    },
    setSelectedStatus: (state, action) => {
      state.selectedStatus = action.payload;
    },
    setInnCode: (state, action) => {
      state.innCode = action.payload;
    },
    setLobbyUser: (state, action) => {
      state.lobbyUser = action.payload;
    },
    setAdmin: (state, action) => {
      state.isAdmin = action.payload;
    },
    setAbovePropertyUser: (state, action) => {
      state.isAbovePropertyUser = action.payload;
      state.isAbovePropertyUserFetched = true;
    },
    setCopyActionPopupEnabled: (state, action) => {
      state.isCopyActionEnabled = action.payload;
    },
    setLoadPages: (state, action) => {
      state.loadPages = action.payload;
    },
    clearOpenSections: (state) => {
      state.openSectionsList = {};
    },
    setOpenSections: (state, action) => {
      state.openSectionsList = { ...state.openSectionsList, ...action.payload };
    },
    setSelectedCards: (state, action) => {
      state.selectedCards.cardList =
        action?.payload.sectionId === state.selectedCards.sectionId
          ? [...(state.selectedCards?.cardList || []), action.payload.cardList]
          : [action.payload.cardList];
      state.selectedCards.sectionId = action?.payload.sectionId;
      state.selectedCards.planId = action?.payload.planId;
    },
    clearSelectedCards: (state, action) => {
      const dataArray = [...state.selectedCards.cardList];
      state.selectedCards.cardList = dataArray.filter((el) => el.id !== action.payload?.id);
    },
    setAllCards: (state, action) => {
      state.selectedCards.cardList = action?.payload.cardList;
      state.selectedCards.sectionId = action?.payload.sectionId;
      state.selectedCards.planId = action?.payload.planId;
    },
    clearAllCardSelection: (state) => {
      state.selectedCards.cardList = [];
      state.selectedCards.sectionId = "";
    },
    setActiveActionCard: (state, action) => {
      state.activeActionCard = action.payload;
    },

    setSearchActive: (state, action) => {
      state.searchActive = action.payload;
    },

    setInncodes: (state, action) => {
      state.innCodes = action.payload;
    },

    setSelectedUsers: (state, action) => {
      state.selectedUser = action.payload;
    },
    setSelectedLeftNav: (state, action) => {
      state.navSelectedDetails.leftNavSelected = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSMEUser.fulfilled, (state, action: any) => {
        state.isSmeUser = true;
        state.isSmeUserFetched = true;
        state.userName = {
          ...state.userName,
          isSmeUser: true
        };
      })
      .addCase(fetchSMEUser.rejected, (state, action: any) => {
        state.isSmeUser = false;
        state.isSmeUserFetched = true;
        state.userName = {
          ...state.userName,
          isSmeUser: false
        };
      })
      .addCase(fetchFilters.fulfilled, (state, action: any) => {
        state.filters = action.payload;
      })
      .addCase(fetchLoggedInUserInfoAsync.fulfilled, (state, action: any) => {
        state.userName = action.payload?.users?.length ? action.payload?.users[0] : {};
      })

      .addCase(editPreferredEmail.pending, (state, action: any) => {
        state.editEmailLoader = true;
      })
      .addCase(editPreferredEmail.fulfilled, (state, action: any) => {
        state.editEmailLoader = false;
        if (state.impersonationDetails.isImpersonated) {
          state.impersonationDetails.userDetails = {
            ...state.impersonationDetails.userDetails,
            ...action.payload.data
          };
        } else {
          state.userName = { ...state.userName, ...action.payload.data };
        }
      })
      .addCase(editPreferredEmail.rejected, (state, action: any) => {
        state.editEmailLoader = false;
      })
      .addCase(deleteActionCardAsync.fulfilled, (state) => {
        state.selectedCards.cardList = [];
        state.selectedCards.sectionId = "";
      })
      .addCase(fetchMyPlanDetailAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.cardsCompletedPrev = action.payload.cardsCompleted;
      })
      .addCase(fetchInncodes.pending, (state, action) => {
        state.innCodeFetching = true;
      })
      .addCase(fetchInncodes.fulfilled, (state, action) => {
        state.innCodes = action.payload.properties || [];
        state.innCodeFetching = false;
      })
      .addCase(fetchInncodes.rejected, (state, action) => {
        state.innCodeFetching = false;
      })

      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.userRoles = action.payload;
      })
      .addCase(editRoleSelection.fulfilled, (state, action) => {
        if (state.impersonationDetails.isImpersonated) {
          state.impersonationDetails.userDetails = { ...state.impersonationDetails.userDetails, ...action.payload };
        } else {
          state.userName = { ...state.userName, ...action.payload };
        }
      })
      .addCase(fetchAllowedRoles.fulfilled, (state, action) => {
        state.loggedInUserDetails.accessRoles = action.payload.accessRoles;
      });
  }
});

export const {
  setImpersonationDetails,
  setCardsCompletedPrev,
  librarySelectedTab,
  clearAllFilters,
  setFiltersApplied,
  searchSelectedTab,
  setSelectedFilters,
  clearSelectedFilters,
  setSelectedFunctionalAreas,
  setSelectedPlanSources,
  setSelectedBrand,
  setSelectedInncodes,
  setCopyActionPopupEnabled,
  setSelectedStatus,
  setInnCode,
  setSearchText,
  setLobbyUser,
  setAdmin,
  setLoadPages,
  clearOpenSections,
  setOpenSections,
  setSelectedCards,
  clearSelectedCards,
  setAllCards,
  clearAllCardSelection,
  setActiveActionCard,
  setSelectedPlanType,
  setSearchActive,
  setInncodes,
  setSelectedUsers,
  setAbovePropertyUser,
  setSelectedLeftNav
} = AppSlice.actions;

export default AppSlice.reducer;
